import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import { useGroupsStore, type Group } from "../../store/admin/groupsStore";
import { useStatusesStore, type Status } from "../../store/admin/statusesStore";
import { useBrandsStore, type Brand } from "../../store/reseller-admin/brandsStore";

interface ListResponse<T> {
  data: T[];
}

export function useAllGroups() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const selectedCompany = useVehiclesSocketStore((s) => s.selectedCompany);
  const cid = selectedCompany || companyId;

  const query = useQuery({
    queryKey: qk.groups.all(cid),
    queryFn: ({ signal }) =>
      getJson<ListResponse<Group>>(
        "/groupList",
        { limit: 2000, page: 1, company_id: cid ?? "" },
        { signal },
        { headers: { "x-company-id": cid ?? "" } },
      ).then((res) => res.data ?? []),
    enabled: !!cid,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (query.data) {
      useGroupsStore.setState({ groups: query.data });
    }
  }, [query.data]);

  return query;
}

export interface GroupListParams {
  page?: number;
  limit?: number;
  status_id?: string;
  search?: string;
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

export interface GroupListResult {
  data: Group[];
  meta?: ListMeta;
}

export function useGroupsList(params: GroupListParams = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.groups.list(companyId, params),
    queryFn: ({ signal }) =>
      getJson<GroupListResult>(
        "/groupList",
        { page: 1, limit: 8, ...params },
        { signal },
      ).then((body) => ({ data: body.data ?? [], meta: body.meta })),
  });
}

export function useStatuses() {
  const query = useQuery({
    queryKey: qk.statuses(),
    queryFn: ({ signal }) =>
      getJson<ListResponse<Status>>(
        "/statusesList",
        { limit: 200, page: 1 },
        { signal },
      ).then((res) => res.data ?? []),
    staleTime: 10 * 60_000,
  });

  useEffect(() => {
    if (query.data) {
      useStatusesStore.setState({ statuses: query.data });
    }
  }, [query.data]);

  return query;
}

export function useAllBrands() {
  const query = useQuery({
    queryKey: qk.brands(),
    queryFn: ({ signal }) =>
      getJson<ListResponse<Brand>>(
        "/brandsList",
        { limit: 500, page: 1 },
        { signal },
      ).then((res) => res.data ?? []),
    staleTime: 10 * 60_000,
  });

  useEffect(() => {
    if (query.data) {
      useBrandsStore.setState({ allBrands: query.data });
    }
  }, [query.data]);

  return query;
}
